import React from 'react';

import type { CompletenessModel, GapsModel, HealthModel } from '@/features/website-dashboard/lib';

import { SectionHeading } from '@/features/website-dashboard/components/ui';
import { fmt, SURFACE_META, TEAL } from '@/features/website-dashboard/lib';

/**
 * The four headline KPIs. All three populated cards read from real
 * derivations; KPI 4 ("Where to look next") is four actionable gaps computed
 * from the breakdown rather than the design's approval-queue counts, which the
 * two endpoints do not expose.
 */

const listPathFor = (surface: string): string =>
  SURFACE_META.find((s) => s.key === surface)?.to ?? '/destination-pages';

const incompletePath = (surface: string, missing?: 'SEO' | 'CONTENT'): string =>
  `${listPathFor(surface)}?f.completeness=${missing ?? 'INCOMPLETE'}&f.status=PUBLISHED`;

/** Open an admin route in a new browser tab (per product/website dashboard UX). */
const openTab = (path: string): void => {
  window.open(path, '_blank', 'noopener,noreferrer');
};

const CardShell = ({
  children,
  accent,
  onClick,
}: {
  children: React.ReactNode;
  accent?: string;
  onClick?: () => void;
}): React.JSX.Element => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      padding: '16px 18px',
      background: '#fff',
      border: '1px solid #e3e5e5',
      borderLeft: accent ? `3px solid ${accent}` : '1px solid #e3e5e5',
      borderRadius: 16,
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    {children}
  </div>
);

const Dot = ({ color }: { color: string }): React.JSX.Element => (
  <span style={{ width: 9, height: 9, borderRadius: 3, background: color }} />
);

const WarnChip = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <span
    style={{
      fontSize: 11,
      color: '#b45309',
      background: '#fef3c7',
      borderRadius: 6,
      padding: '3px 8px',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

interface Props {
  health: HealthModel;
  completeness: CompletenessModel;
  gaps: GapsModel | null;
}

export const SiteHealthKpis = ({ health, completeness, gaps }: Props): React.JSX.Element => {
  // Donut: r=27 circumference is ~170; offset shrinks the green arc to the pct.
  const completeDash = Math.round(170 * (1 - completeness.completePct / 100));

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionHeading title="Site health" />
      <div className="wd-kpis" style={{ display: 'grid', gap: 10 }}>
        {/* KPI 1 - live on the site */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            padding: '16px 18px',
            background: TEAL,
            borderRadius: 16,
            color: '#fff',
            boxShadow: '0 18px 36px -24px rgba(12,59,77,.7)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              right: -50,
              top: -50,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: 'radial-gradient(circle,rgba(255,255,255,.12),rgba(255,255,255,0) 70%)',
            }}
          />
          <div style={{ fontSize: 12, fontWeight: 700, color: '#b9d2db' }}>Live on the site</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span
              style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-.04em', lineHeight: '.9' }}
            >
              {fmt(health.liveTotal)}
            </span>
            <span style={{ fontSize: 12, color: '#b9d2db' }}>of {fmt(health.grandTotal)} rows</span>
          </div>
          <div style={{ display: 'flex', height: 6, gap: 3 }}>
            <span style={{ width: `${health.livePct}%`, background: '#fff', borderRadius: 3 }} />
            <span style={{ flex: 1, background: 'rgba(255,255,255,.25)', borderRadius: 3 }} />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: '#b9d2db',
              whiteSpace: 'nowrap',
            }}
          >
            <span>
              Draft <b style={{ color: '#fff', fontWeight: 800 }}>{fmt(health.draftTotal)}</b>
            </span>
            <span>
              Pending review{' '}
              <b style={{ color: '#fff', fontWeight: 800 }}>{fmt(health.reviewTotal)}</b>
            </span>
            <span>
              Archived <b style={{ color: '#fff', fontWeight: 800 }}>{fmt(health.otherTotal)}</b>
            </span>
          </div>
        </div>

        {/* KPI 2 - live pages missing SEO / content. The headline number is a
            cross-surface total, so the CARD does not navigate (there is no
            single list that shows all of it); the per-surface + SEO/content
            chips below are the drill-downs, each matching the list it opens. */}
        <CardShell accent="#f59e0b">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 12,
              fontWeight: 700,
              color: '#55595e',
            }}
          >
            <Dot color="#f59e0b" />
            Live pages missing SEO / content
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span
              style={{
                fontSize: 32,
                fontWeight: 800,
                letterSpacing: '-.03em',
                color: '#b45309',
                lineHeight: 1,
              }}
            >
              {fmt(health.incompleteLive)}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#b45309',
                background: '#fef3c7',
                borderRadius: 999,
                padding: '2px 7px',
              }}
            >
              {health.incompleteLivePct}% of live
            </span>
          </div>
          {/*
            SEO / content split. Only DESTINATION PAGES can be filtered by
            SEO / content, so the clickable number is destination-only (it
            matches the list it opens); the cross-surface total is shown as
            muted context. Per-surface drill-downs are the chips below.
          */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openTab(incompletePath('destination_page', 'SEO'));
              }}
              title="Open destination pages missing SEO (opens in a new tab)"
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#b45309',
                background: '#fef3c7',
                border: '1px solid #fcd9a1',
                borderRadius: 999,
                padding: '2px 9px',
                cursor: 'pointer',
              }}
            >
              {fmt(health.missingSeoLiveDp)} missing SEO
              {health.missingSeoLive > health.missingSeoLiveDp && (
                <span style={{ fontWeight: 600, opacity: 0.7 }}>
                  {' '}
                  · {fmt(health.missingSeoLive)} all live
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openTab(incompletePath('destination_page', 'CONTENT'));
              }}
              title="Open destination pages missing content (opens in a new tab)"
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#55595e',
                background: '#f1f3f4',
                border: '1px solid #e3e5e5',
                borderRadius: 999,
                padding: '2px 9px',
                cursor: 'pointer',
              }}
            >
              {fmt(health.missingContentLiveDp)} missing content
              {health.missingContentLive > health.missingContentLiveDp && (
                <span style={{ fontWeight: 600, opacity: 0.7 }}>
                  {' '}
                  · {fmt(health.missingContentLive)} all live
                </span>
              )}
            </button>
          </div>
          {/* Per-surface incomplete drill-downs - each opens that surface's
              incomplete list (every surface supports the incomplete filter),
              so the number always matches the list it opens. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {health.incompleteBySurface.length === 0 ? (
              <span style={{ fontSize: 11, color: '#8a9095' }}>No live pages with gaps.</span>
            ) : (
              health.incompleteBySurface.map((s) => (
                <button
                  key={s.surface}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openTab(incompletePath(s.surface));
                  }}
                  title={`Open incomplete ${s.label} (opens in a new tab)`}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#55595e',
                    background: '#f6f7f8',
                    border: '1px solid #e3e5e5',
                    borderRadius: 999,
                    padding: '2px 9px',
                    cursor: 'pointer',
                  }}
                >
                  <b style={{ color: '#17171a', fontWeight: 700 }}>{fmt(s.live)}</b> {s.label}
                </button>
              ))
            )}
          </div>
          <div style={{ fontSize: 11, color: '#8a9095' }}>
            + {fmt(health.incompleteDraft)} drafts with gaps (expected)
          </div>
        </CardShell>

        {/* KPI 3 - destination pages complete */}
        <CardShell accent="#10b981">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 12,
              fontWeight: 700,
              color: '#55595e',
            }}
          >
            <Dot color="#10b981" />
            Destination pages complete
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ position: 'relative', width: 64, height: 64, flexShrink: 0 }}>
              <svg
                viewBox="0 0 64 64"
                width="64"
                height="64"
                style={{ transform: 'rotate(-90deg)' }}
              >
                <circle cx="32" cy="32" r="27" fill="none" stroke="#e6f4ef" strokeWidth="7" />
                <circle
                  cx="32"
                  cy="32"
                  r="27"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="7"
                  strokeLinecap="round"
                  strokeDasharray="170"
                  strokeDashoffset={completeDash}
                />
              </svg>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 800,
                  color: '#047857',
                }}
              >
                {completeness.completePct}%
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                fontSize: 12,
                color: '#55595e',
                whiteSpace: 'nowrap',
              }}
            >
              <span>
                <b style={{ fontSize: 18, fontWeight: 800, color: '#17171a' }}>
                  {fmt(completeness.completeCount)}
                </b>{' '}
                of {fmt(completeness.dpTotal)} pages
              </span>
              <span style={{ fontSize: 11, color: '#8a9095' }}>
                {fmt(completeness.incompleteCount)} incomplete ·{' '}
                {fmt(completeness.liveIncompleteCount)} of them live
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {completeness.ruleFails
              .filter((r) => r.n > 0)
              .map((r) => (
                <WarnChip key={r.key}>
                  <b style={{ fontWeight: 800 }}>{fmt(r.n)}</b> {r.label}
                </WarnChip>
              ))}
          </div>
        </CardShell>

        {/* KPI 4 - where to look next (real gaps, not approval queues) */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '16px 18px',
            background: '#fff',
            border: '1px solid #e3e5e5',
            borderRadius: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 12,
              fontWeight: 700,
              color: '#55595e',
            }}
          >
            <Dot color={TEAL} />
            Where to look next
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
              gap: '8px 12px',
            }}
          >
            {/* These gaps are computed with collection / blog / thin rules that
                no list page can filter by, so each number would not match the
                list it opened. They are read-only insights; the Worklist below
                ("Selling with no live page", "Needs attention") is the
                actionable drill-down with per-item links. */}
            <GapStat
              value={gaps?.sellingNoPage ?? 0}
              label="selling, no live page"
              color="#b91c1c"
            />
            <GapStat
              value={gaps?.liveIncomplete ?? 0}
              label="live pages incomplete"
              color="#b45309"
            />
            <GapStat
              value={gaps?.blogless ?? 0}
              label="destinations with no blog"
              color="#6d28d9"
            />
            <GapStat
              value={gaps?.thinCategoryPages ?? 0}
              label="thin category pages"
              color="#17171a"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const GapStat = ({
  value,
  label,
  color,
}: {
  value: number;
  label: string;
  color: string;
}): React.JSX.Element => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.03em', color, lineHeight: 1 }}>
      {fmt(value)}
    </span>
    <span style={{ fontSize: 11, color: '#55595e', lineHeight: 1.3 }}>{label}</span>
  </div>
);
